<template>
    <section class="carousel-component">
        <div class="height-container">
            <div class="carousel-inner" ref="carouselWrapperRef">
                <div
                    v-for="(item, index) in filteredPhotos"
                    :key="item"
                    class="carousel-item"
                    :class="index === 0 ? 'is-hero' : 'is-letterboxed'"
                    ref="slideRef"
                    :data-num="index"
                >
                    <mess-image placeholder :src="getImageUrl(item)" :loading="index === 0 ? 'eager' : 'lazy'" />
                </div>
            </div>

            <ul v-if="filteredPhotos && filteredPhotos.length > 1" class="dots">
                <li>
                    <!-- An arrow that can't go anywhere fades out rather than sitting
                         there live. `inert` also takes it out of the tab order and
                         stops clicks landing on something invisible. -->
                    <button
                        v-if="filteredPhotos && filteredPhotos.length > 1"
                        class="arrow"
                        :class="{ 'is-hidden': atStart }"
                        :inert="atStart"
                        aria-label="Previous photo"
                        @click="step(-1)">
                        <SvgsLeftArrow />
                    </button>
                </li>
                <li v-for="(item, index) in filteredPhotos" :key="`${item}-dot`">
                    <button
                        type="button"
                        :aria-label="`Show image ${index + 1} of ${filteredPhotos.length}`"
                        :aria-current="active === index ? 'true' : undefined"
                        @click="navigateTo(index)">
                        <span :class="active === index ? 'active' : ''"></span>
                    </button>
                </li>
                <li>
                    <button
                        v-if="filteredPhotos && filteredPhotos.length > 1"
                        class="arrow"
                        :class="{ 'is-hidden': atEnd }"
                        :inert="atEnd"
                        aria-label="Next photo"
                        @click="step(1)">
                        <SvgsRightArrow />
                    </button>
                </li>
            </ul>

            <button
                type="button"
                @click="toggleCredit(curr)"
                v-if="credits?.length > 0 && credits[curr]"
                class="credit-icon"
                aria-label="Image credit"
                :aria-expanded="String(showCredit)">
                <SvgsInfo v-if="!showCredit" />
                <SvgsCloseCircle v-if="showCredit" />
            </button>
        </div>

        <div v-if="showCredit" class="credit">
            <p class="tiny-book italic" v-html="credit"></p>
        </div>
    </section>
</template>

<script setup>
    const { getImageUrl } = useImageUrl();

    const props = defineProps({
        photos: {
            type: Array,
            required: false,
        },
        credits: {
            type: Array,
            required: false,
            default: [],
        },
    });

    const { photos, credits } = toRefs(props);

    const filteredPhotos = computed(() => {
        return photos.value.filter((photo) => {
            // Check if photo has any valid URL (R2, thumbnail, or direct)
            return getImageUrl(photo) !== '';
        });
    });

    // Variables
    const active = ref(0);
    const slideRef = ref([]);
    const carouselWrapperRef = ref(null);
    const observer = ref(null);
    // Whether the strip is scrolled hard against either end. Read from scroll
    // position rather than `active`: `active` is set by the IntersectionObserver
    // below, which is wired up once on mount, so it goes stale if the observer
    // never attaches — and a stale index is what leaves a dead arrow live.
    const atStart = ref(true);
    const atEnd = ref(false);
    const showCredit = ref(false);
    const credit = ref('');
    const curr = ref(0);

    const toggleCredit = (index) => {
        showCredit.value = !showCredit.value;
        credit.value = credits.value[index];
    };

    const setUpIntersectionObserver = () => {
        
        const options = {
            root: carouselWrapperRef.value,
            threshold: 0.9,
        };

        observer.value = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                const current = parseInt(entry.target.getAttribute('data-num'), 10);
                if (entry.isIntersecting) {
                    active.value = current;
                    if (credits?.value?.length > 0) {
                        if (showCredit.value == true) credit.value = credits.value[current];
                        curr.value = current;
                    }
                }
            });
        }, options);

        slideRef.value.forEach((item) => {
            observer.value.observe(item);
        });
    };

    const updateEdges = () => {
        const el = carouselWrapperRef.value;
        if (!el) return;
        const edges = carouselEdges(el);
        atStart.value = edges.atStart;
        atEnd.value = edges.atEnd;

        // Keep `active` in step with where the strip actually is.
        //
        // It used to be written ONLY by the IntersectionObserver below, which
        // was not firing — so it sat at 0 while the strip scrolled away, and
        // navigateTo(active - 1) became navigateTo(-1), clamped to 0, saw
        // diff === 0 and returned. The "previous" arrow was correctly visible,
        // because its visibility is scroll-derived, and did nothing when
        // clicked: 349 such clicks from 80 people in 36 hours, every one
        // reporting from_index 0. "Next" hid the problem because scrollBy is
        // relative and advanced a width regardless.
        //
        // This runs on the same scroll listener that already keeps atStart and
        // atEnd correct, so it is driven by a signal proven to be working. The
        // observer still updates `active` too, which is harmless — both now
        // derive from the same scroll position and agree.
        active.value = activeSlideFrom(el, filteredPhotos.value.length);
        // Arrived where the last click was heading: hand back to `active`.
        if (pendingTarget !== null && active.value === pendingTarget && Math.abs(el.scrollLeft - slideLeft(el, pendingTarget)) <= 2) {
            pendingTarget = null;
        }
    };

    // Where the last arrow/dot click sent the strip, until the scroll gets
    // there. `active` follows the scroll position, so mid-animation it still
    // names the slide being left. Arrows used to step from it with a relative
    // scrollBy, so a second click during the smooth scroll stepped from the
    // same stale slide, from a half-scrolled position: measured on production,
    // three clicks 120-250ms apart advanced two photos, the site page carousel
    // being the largest source of rage clicks on the site (~2,150 people a
    // week). Clicks now chain from the pending target and scroll to an
    // absolute slide position.
    let pendingTarget = null;
    let pendingTimer = 0;

    function slideLeft(el, index) {
        const slide = slideRef.value?.[index];
        if (!slide) return Math.floor(el.clientWidth) * index;
        return slide.getBoundingClientRect().left - el.getBoundingClientRect().left + el.scrollLeft;
    }

    const navigateTo = (i) => {
        const el = carouselWrapperRef.value;
        if (!el) return;
        // Clamp: the arrows are inert at the ends, but the dots and quick
        // repeated clicks can still ask for a slide past either end.
        const total = filteredPhotos.value.length;
        const target = Math.min(Math.max(i, 0), total - 1);
        if (target === (pendingTarget ?? active.value)) return;

        pendingTarget = target;
        el.scrollTo({ left: slideLeft(el, target), behavior: 'smooth' });
        // Normally released by updateEdges on arrival; this is the backstop
        // for a scroll that never quite lands (the user grabbed the strip).
        clearTimeout(pendingTimer);
        pendingTimer = window.setTimeout(() => {
            pendingTarget = null;
        }, 1500);
    };

    const step = (delta) => navigateTo((pendingTarget ?? active.value) + delta);

    onMounted(() => {
        nextTick(() => {
            if (import.meta.client) {
                if (carouselWrapperRef.value && slideRef.value.length) {
                    setUpIntersectionObserver();
                    updateEdges();
                    carouselWrapperRef.value.addEventListener('scroll', updateEdges, { passive: true });
                    window.addEventListener('resize', updateEdges, { passive: true });
                }
            }
        });
    });

    onBeforeUnmount(() => {
        if (import.meta.client) {
            if (observer.value) {
                observer.value.disconnect();
            }
            if (carouselWrapperRef.value) {
                carouselWrapperRef.value.removeEventListener('scroll', updateEdges);
            }
            window.removeEventListener('resize', updateEdges);
            clearTimeout(pendingTimer);
            active.value = 0;
            slideRef.value = [];
            carouselWrapperRef.value = null;
        }
    });
</script>

<style lang="scss" scoped>
    .carousel-component {
        position: relative;

        .height-container {
            height: 360px;
            position: relative;

            @media (min-width: $tablet) {
                height: 500px;
            }

            &::after {
                display: block;
                content: '';
                position: absolute;
                bottom: 0;
                left: 0;
                transform: scale(-1);
                width: 100%;
                z-index: 1;
                height: 72px;
                background: linear-gradient(180deg, #000 0%, rgba(0, 0, 0, 0.56) 58.85%, rgba(0, 0, 0, 0) 100%);
            }
        }

        .carousel-inner {
            display: flex;
            flex-wrap: nowrap;
            overflow-x: scroll;
            overflow-y: hidden;
            -webkit-overflow-scrolling: touch;
            scroll-snap-type: x mandatory;
            scroll-behavior: smooth;
            -webkit-overflow-scrolling: touch;
            -ms-overflow-style: none;
            scrollbar-width: none;
            align-items: flex-start;
            margin: 0;
            height: 360px;

            @media (min-width: $tablet) {
                height: 500px;
            }

            &::-webkit-scrollbar {
                display: none;
            }

            .carousel-item {
                flex: 0 0 100%;
                width: 100%;
                height: 100%;
                scroll-snap-align: start;
                position: relative;

                img {
                    height: 100%;
                    width: 100%;
                    object-position: center;
                }

                // Slide 1 is the hero and must read as landscape, so it keeps
                // filling the frame even when that means cropping.
                &.is-hero img {
                    object-fit: cover;
                }

                // Slides 2+ can be any aspect ratio — portrait shots included —
                // so show the whole image inside the same fixed-height frame and
                // let the backdrop fill what's left. Black rather than grey
                // because .height-container already lays a black gradient over
                // the bottom 72px for the dots/credit button; on grey that scrim
                // would read as a smudge.
                &.is-letterboxed {
                    background-color: $black;

                    img {
                        object-fit: contain;
                    }
                }
            }
        }

        .dots {
            position: absolute;
            z-index: 3;
            left: 0;
            right: 0;
            bottom: 18px;
            display: flex;
            justify-content: center;
            align-items: center;
            flex-wrap: wrap;
            button {
                width: 15px;
                height: 15px;
                display: flex;
                flex-direction: column;
                span {
                    width: 8px;
                    height: 8px;
                    display: block;
                    margin: auto;
                    border-radius: 50%;
                    background: $gray-light-2;
                    &.active {
                        background: $white;
                    }
                }
            }
        }
        .credit-icon {
            position: absolute;
            bottom: 16px;
            right: 15px;
            z-index: 4;
        }
        .credit {
            position: relative;
            padding: 15px;
            background-color: $gray-light;
        }

        @media (min-width: $tablet) {
            .height-container {
                margin-top: 60px;
            }
        }

        button.arrow {
            display: none;
            background: white;
            padding: 10px;
            border-radius: 50%;

            opacity: 1;
            visibility: visible;
            transition: opacity 0.2s ease, visibility 0s linear 0s, background-color 0.2s ease;

            // Fill with teal and flip the mark to white, matching
            // Global/carousel.vue and the map's Download PDF button.
            //
            // The stroke has to be overridden in CSS because left-arrow.vue and
            // right-arrow.vue hardcode stroke="black" as a presentation
            // attribute, and a CSS rule outranks one.
            //
            // :deep() is required. This block is scoped, and the paths belong to
            // the SvgsLeftArrow/SvgsRightArrow child components, which carry
            // their own scope id — so a plain `svg path` compiles to
            // `path[data-v-<hero>]` and matches nothing, leaving a teal circle
            // with a black arrow in it.
            :deep(svg path) {
                transition: stroke 0.2s ease;
            }

            @media (hover: hover) {
                &:hover {
                    background-color: $turquoise;

                    :deep(svg path) {
                        stroke: white;
                    }
                }
            }

            // These arrows appear from $tablet up, and a tablet has no hover —
            // so on touch the resting state wears the treatment permanently
            // rather than being undiscoverable.
            @media (hover: none) {
                background-color: $turquoise;

                :deep(svg path) {
                    stroke: white;
                }
            }

            &.is-hidden {
                opacity: 0;
                visibility: hidden;
                // Delay the visibility flip until the fade has finished.
                transition: opacity 0.2s ease, visibility 0s linear 0.2s;
            }

            @media (prefers-reduced-motion: reduce) {
                transition: none;

                &.is-hidden {
                    transition: none;
                }
            }

            svg {
                width: 22px;
            }
        }

        @media (min-width: $tablet) {
            .button-wrapper {
                display: flex;
            }

            button.arrow {
                display: block;
                width: auto;
                height: auto;
                margin: 0 20px;
            }
        }
    }
</style>
